import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentWorkspaceId } from '@/lib/workspace-utils';

export interface MessageTemplate {
  id: string;
  workspace_id?: string;
  title: string;
  tag: string;
  body: string;
  categoria?: string;
  tipo_midia?: string;
  midia_url?: string;
  tipo_agro?: string;
  tipo_disparo?: string | null;
  variaveis_disponiveis?: string[];
  usage_count?: number;
  created_at?: string;
  updated_at?: string;
}

export function useMessageTemplates() {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const workspaceId = await getCurrentWorkspaceId();
      
      if (!workspaceId) {
        setTemplates([]);
        return;
      }

      // Usar RPC que bypassa RLS para autenticação via token
      const { data, error: fetchError } = await supabase
        .rpc('get_workspace_templates', { p_workspace_id: workspaceId });

      if (fetchError) throw fetchError;
      setTemplates(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async (template: Partial<MessageTemplate>) => {
    try {
      const workspaceId = await getCurrentWorkspaceId();
      if (!workspaceId) throw new Error('No workspace found');

      // Try direct insert first
      const { data, error } = await supabase
        .from('message_templates')
        .insert([{
          title: template.title!,
          tag: template.tag!,
          body: template.body!,
          categoria: template.categoria,
          tipo_midia: template.tipo_midia,
          midia_url: template.midia_url,
          tipo_disparo: template.tipo_disparo,
          workspace_id: workspaceId,
        }])
        .select()
        .single();

      // If RLS blocks, use RPC fallback
      if (error && (error.code === '42501' || error.message?.includes('row-level security'))) {
        console.log('RLS blocked template creation, using RPC fallback...');
        
        const { data: rpcData, error: rpcError } = await supabase.rpc('create_workspace_template', {
          p_workspace_id: workspaceId,
          p_title: template.title!,
          p_tag: template.tag!,
          p_body: template.body!,
          p_categoria: template.categoria || null,
          p_tipo_midia: template.tipo_midia || null,
          p_midia_url: template.midia_url || null,
          p_tipo_disparo: template.tipo_disparo || null
        });
        
        if (rpcError) throw rpcError;
        
        await fetchTemplates();
        return { id: rpcData };
      }

      if (error) throw error;
      
      await fetchTemplates();
      return data;
    } catch (err) {
      console.error('Error creating template:', err);
      throw err;
    }
  };

  const updateTemplate = async (id: string, updates: Partial<MessageTemplate>) => {
    try {
      const { error } = await supabase
        .from('message_templates')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await fetchTemplates();
    } catch (err) {
      console.error('Error updating template:', err);
      throw err;
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      const { error } = await supabase
        .from('message_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchTemplates();

    // Set up real-time subscription
    const channel = supabase
      .channel('message_templates_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_templates',
        },
        () => {
          fetchTemplates();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    templates,
    loading,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    refetch: fetchTemplates,
  };
}
